import { useQuery } from '@tanstack/react-query';
import type { ReportTemplate } from '@safetyhub/shared';
import { listEnabledTemplates } from '../lib/report-composer';

export interface UseEnabledComposerTemplatesOptions {
  /**
   * 이 화면이 지금 목록을 필요로 하는가 (기본 true).
   *
   * 모달처럼 닫혀 있는 동안 요청을 만들지 않아야 하는 자리가 있어 축을 밖으로 뺐다.
   * false면 요청하지 않고 목록을 비운다.
   */
  enabled?: boolean;
}

export interface UseEnabledComposerTemplatesResult {
  /** 지금 이 사용자에게 **사용 가능한** Composer 템플릿 (`GET /report-composer/templates`) */
  templates: ReportTemplate[];
  /** 판정 재료가 아직 확정되지 않았는가 — "아직 모른다"와 "정말 없다"를 가르는 자리 */
  isLoading: boolean;
}

/** 참조가 흔들리지 않도록 빈 결과는 한 배열을 돌려쓴다(소비처의 useMemo 의존성). */
const EMPTY: ReportTemplate[] = [];

/**
 * 캐시 수명 — 피처 플래그 훅(`useFeatureFlags`)의 `staleTime`과 같은 값이다. 둘 다 "지금 이 사용자에게
 * 무엇이 켜져 있나"라 수명을 맞춘 것이고, 이 훅이 플래그를 읽는다는 뜻은 아니다(플래그 게이트는 아래 절).
 *
 * ⚠️ **쿼리에 명시**한다. 이 앱은 `App.tsx`에 중첩 `QueryClientProvider`가 있고, 그 기본
 * `staleTime`(5분)이 `main.tsx`의 0을 가린 사고가 있었다(2026-09-07 F-9). 기본값에 기대면
 * 어느 provider 밑에 걸리느냐에 따라 수명이 달라진다.
 */
const TEMPLATES_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * enabled Composer 템플릿 목록을 읽는 공용 훅.
 *
 * ## 왜 훅인가
 * 같은 fetch가 문서 유형 선택 모달과 생성 위저드에 **거의 같은 코드로 복제**돼 있었고,
 * 여기에 문서관리 목록(유형 필터 선택지)이 소비처로 더 붙는다. 세 곳이 서로 다른 폴백을 들면
 * 한 surface만 조용히 옛 동작에 남는다.
 *
 * ## 피처 플래그 게이트를 두지 않는다
 * 목록 조회를 플래그 뒤에 두면 「아직 모른다(플래그 로딩)」와 「플래그 off」를 갈라 접어 올려야
 * 한다. 모든 문서가 Composer 문서라 그 플래그는 항상 ON이라야 앱이 성립하므로(꺼지면 문서를
 * 하나도 만들 수 없다) **항상 조회**한다 — 「아직 모른다」의 축은 템플릿 목록 하나다.
 *
 * ## `useQuery`인 이유
 * 이 응답은 템플릿 **통짜**라 무겁다(dev enabled 5건 합 123 kB). `useEffect` fetch는 캐시가
 * 없어 마운트마다 그만큼을 다시 받는데, 소비처 중 하나가 앱에서 제일 자주 여는 문서관리
 * 목록이다. 캐시를 두면 화면 간에 공유되고, 첫 조회 동안 `isLoading`이 참이라
 * "첫 렌더 한 프레임"도 초기값 손질 없이 닫힌다.
 *
 * ## 폴백 규약 (바꾸지 말 것)
 * - 조회 실패 → **빈 배열**(재시도 없음 — 종전 `.catch(() => [])`와 같다).
 *   네트워크 사고로 진입로가 사라지는 것보다 낫고, 게이트의 대기도 길어지지 않는다.
 * - `enabled` false → 요청하지 않고 빈 배열, `isLoading` false.
 */
export function useEnabledComposerTemplates(
  companyId: string | undefined,
  { enabled = true }: UseEnabledComposerTemplatesOptions = {},
): UseEnabledComposerTemplatesResult {
  const { data, isLoading: isTemplatesLoading } = useQuery({
    // companyId별로 가른다 — 서버가 조직 노출 정책으로 필터한 목록이라 섞이면 안 된다.
    queryKey: ['report-composer', 'enabled-templates', companyId ?? null],
    queryFn: () => listEnabledTemplates(companyId),
    enabled,
    staleTime: TEMPLATES_STALE_TIME_MS,
    retry: false,
  });

  return {
    templates: enabled ? (data ?? EMPTY) : EMPTY,
    isLoading: enabled && isTemplatesLoading,
  };
}
